import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { useAuth } from './AuthContext';
import { rememberEntitlement, readRememberedEntitlement } from './utils/entitlementCache';

const REVENUECAT_PUBLIC_KEY_IOS     = process.env.REACT_APP_RC_IOS_KEY     || '';
const REVENUECAT_PUBLIC_KEY_ANDROID = process.env.REACT_APP_RC_ANDROID_KEY || '';
const PRO_ENTITLEMENT = '1to19golf Pro';

const SubscriptionContext = createContext({
  isReady: false,
  isEntitled: false,
  isBypassed: false,
  customerInfo: null,
  offerings: null,
  proPackage: null,
  purchase: async () => {},
  restore: async () => {},
  refresh: async () => {},
  error: null,
  welcome: false,
  finishWelcome: () => {},
});

export const useSubscription = () => useContext(SubscriptionContext);

export function SubscriptionProvider({ children }) {
  const { currentUser, userProfile } = useAuth();
  const [isReady, setIsReady]           = useState(false);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [offerings, setOfferings]       = useState(null);
  const [error, setError]               = useState(null);
  // True from the moment a purchase or restore unlocks the app until the
  // paywall has shown "You're in" and faded. The gate keeps the paywall up
  // while it is set; without it the paywall vanished mid-"Starting…" the
  // instant RevenueCat answered, and Home simply appeared.
  const [welcome, setWelcome]           = useState(false);
  const finishWelcome = useCallback(() => setWelcome(false), []);

  // The server's answer (plans.py::bypasses_paywall): system admins and a Pro
  // plan granted by hand. This used to test `effective_plan === 'pro'`, a key
  // /auth/me never sent, so an admin-granted Pro plan still met the paywall.
  const isBypassed = !!(userProfile?.is_system_admin || userProfile?.bypasses_paywall);
  const isNative = Capacitor.isNativePlatform();

  const refresh = useCallback(async () => {
    if (!isNative) { setIsReady(true); return; }
    try {
      const info = await Purchases.getCustomerInfo();
      setCustomerInfo(info?.customerInfo ?? info);
      const offs = await Purchases.getOfferings();
      setOfferings(offs?.offerings ?? offs);
    } catch (e) {
      console.warn('RC refresh failed:', e);
      setError(e?.message || String(e));
    } finally {
      setIsReady(true);
    }
  }, [isNative]);

  useEffect(() => {
    if (!isNative) { setIsReady(true); return; }
    if (!currentUser) return;

    if (!REVENUECAT_PUBLIC_KEY_IOS && !REVENUECAT_PUBLIC_KEY_ANDROID) {
      console.warn('SubscriptionContext: no RC API keys configured; skipping init.');
      setIsReady(true);
      return;
    }

    let cancelled = false;

    const safetyTimeout = setTimeout(() => {
      if (!cancelled) {
        console.warn('SubscriptionContext: init timed out after 8s; forcing ready.');
        setIsReady(true);
      }
    }, 8000);

    (async () => {
      const apiKey = Capacitor.getPlatform() === 'ios'
        ? REVENUECAT_PUBLIC_KEY_IOS
        : REVENUECAT_PUBLIC_KEY_ANDROID;

      if (!apiKey) {
        console.warn('RevenueCat API key missing for platform', Capacitor.getPlatform());
        clearTimeout(safetyTimeout);
        setIsReady(true);
        return;
      }

      try {
        console.log('[RC] calling configure, platform:', Capacitor.getPlatform(), 'key prefix:', apiKey.slice(0, 12));
        await Purchases.configure({
          apiKey,
          appUserID: currentUser.uid,
        });
        console.log('[RC] configure done');
        if (cancelled) return;
        const info = await Purchases.getCustomerInfo();
        console.log('[RC] getCustomerInfo done');
        if (cancelled) return;
        setCustomerInfo(info?.customerInfo ?? info);
        const offs = await Purchases.getOfferings();
        console.log('[RC] getOfferings done');
        if (cancelled) return;
        setOfferings(offs?.offerings ?? offs);
      } catch (e) {
        console.warn('[RC] init failed:', e);
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        clearTimeout(safetyTimeout);
        if (!cancelled) setIsReady(true);
      }
    })();

    return () => { cancelled = true; clearTimeout(safetyTimeout); };
  }, [currentUser, isNative]);

  const purchase = useCallback(async (pkg) => {
    setError(null);
    if (!Purchases) throw new Error('Purchases unavailable');
    const target = pkg || getPkgFromOfferings(offerings);
    if (!target) throw new Error('No subscription package available');
    const result = await Purchases.purchasePackage({ aPackage: target });
    const info = result?.customerInfo ?? result;
    if (hasProEntitlement(info)) setWelcome(true);
    setCustomerInfo(info);
    return result;
  }, [offerings]);

  const restore = useCallback(async () => {
    setError(null);
    if (!Purchases) throw new Error('Purchases unavailable');
    const result = await Purchases.restorePurchases();
    const info = result?.customerInfo ?? result;
    if (hasProEntitlement(info)) setWelcome(true);
    setCustomerInfo(info);
    return result;
  }, []);

  // A LIVE answer from RevenueCat, or `null` when the SDK has not managed to
  // give us one (init failed, or the 8s timeout fired). The distinction is the
  // whole point: `null` is "we don't know", which used to be treated as "not a
  // subscriber" and locked paying customers out of the entire app.
  const liveEntitled = customerInfo ? hasProEntitlement(customerInfo) : null;

  // Remember every live answer, in both directions — the snapshot has to be
  // able to say "no" as well, or a lapsed subscriber's last "yes" would sit
  // there for the whole TTL.
  useEffect(() => {
    if (liveEntitled === null || !currentUser?.uid) return;
    rememberEntitlement(currentUser.uid, liveEntitled);
  }, [liveEntitled, currentUser]);

  // Only consulted when there is no live answer at all. Read per uid so
  // signing in as someone else can never inherit their entitlement.
  const remembered = useMemo(
    () => readRememberedEntitlement(currentUser?.uid),
    // Deliberately not re-read when the snapshot is written: a live answer
    // outranks it anyway, so a stale read here can never be consulted.
    [currentUser],
  );

  const entitled = isBypassed
    || (liveEntitled !== null ? liveEntitled : remembered === true);

  return (
    <SubscriptionContext.Provider value={{
      isReady: isReady || !isNative,
      isEntitled: entitled,
      isBypassed,
      customerInfo,
      offerings,
      proPackage: getPkgFromOfferings(offerings),
      purchase,
      restore,
      refresh,
      error,
      welcome,
      finishWelcome,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

function hasProEntitlement(customerInfo) {
  if (!customerInfo) return false;
  const ent = customerInfo.entitlements;
  const active = ent?.active || {};
  // Primary: the named Pro entitlement is active.
  if (active[PRO_ENTITLEMENT]) return true;
  // Defensive: any active entitlement unlocks Pro. Guards against the
  // entitlement identifier in the RC dashboard not matching PRO_ENTITLEMENT.
  if (Object.keys(active).length > 0) return true;
  // Last resort: an active subscription product exists (single-product app),
  // so a paying user is never locked out by a missing entitlement mapping.
  const subs = customerInfo.activeSubscriptions || [];
  return subs.length > 0;
}

function getPkgFromOfferings(offerings) {
  if (!offerings) return null;
  const current = offerings.current || Object.values(offerings.all || {})[0];
  if (!current) return null;
  return current.annual
      || current.availablePackages?.find(p => p.packageType === 'ANNUAL')
      || current.availablePackages?.[0]
      || null;
}
